import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// axios
import { api } from "@/api/axios";

// Context
import { useAuth } from '@/auth/AuthContext';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// custom hooks
import useCurrentBreakpoint from "@/hooks/useCurrentBreakpoint";
import useSSE from '@/hooks/useSSE';

// assets
import BellIcon from 'assets/header-icons/BellIcon';
import ResultAnalysIcon from 'assets/medCardPageIcons/ResultAnalysIcon';
import HospitalizationIcon from 'assets/medCardPageIcons/HospitalizationIcon';
import ResearchIcon from 'assets/medCardPageIcons/ResearchIcon';
import RecipeIcon from 'assets/medCardPageIcons/RecipeIcon';
import ExaminationIcon from 'assets/medCardPageIcons/ExaminationsIcon';
import DirectionIcon from 'assets/medCardPageIcons/DirectionIcon';
import RecommendationsIcon from 'assets/medCardPageIcons/RecommendationsIcon';
import MyAppointments from 'assets/menu-icons/MyAppointments';
import FeedbackIcon from 'assets/menu-icons/MessageMedical';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

const BASE_BY_BP = { 
  xs: 3,
  sm: 3,
  md: 5,
  xl: 5, 
  lg: 5
}

// Список редиректов в зависимости от типа уведомления
const notificationTypes = {
  RECOMMENDATIONS: '/medCard/recommendations', // Рекомендации
  ST_PRIEM: '/medCard/examinations', // Осмотры
  ST_DIAG: '/medCard/researches', // Исследования
  ST_LAB: '/medCard/analysisResults', // Результаты анализов
  DRUG_TREATMENT: '/medCard/recipes', // Рецепты
  EPIKRIZ: '/medCard/hospitalizations', // Госпитализации
  DIRECTIONS: '/medCard/directions', // Направления
  FEEDBACK: '/chatFeedBack' // Обратная связь
};

// Список иконок в зависимости от типа уведомления
const iconByType = {
  RECOMMENDATIONS: RecommendationsIcon, // Рекомендации
  ST_PRIEM: ExaminationIcon, // Осмотры
  ST_DIAG: ResearchIcon, // Исследования
  ST_LAB: ResultAnalysIcon, // Результаты анализов
  DRUG_TREATMENT: RecipeIcon, // Рецепты
  EPIKRIZ: HospitalizationIcon, // Госпитализации
  DIRECTIONS: DirectionIcon, // Направления
  FEEDBACK: FeedbackIcon, // Обратная связь
}

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {

  const sessionIdRef = useRef(null);

  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();  
  const anchorRef = useRef(null);
  const currentBreakpoint = useCurrentBreakpoint();

  const [read, setRead] = useState(0);
  const [open, setOpen] = useState(false);
  // const [notificationList, setNotificationList] = useState([]);
  const { notificationList, setNotificationList } = useAuth();

  const { notifications, reconnect, closeConnection } = useSSE({
    url: '/session/events', 
    sid: sessionIdRef.current
  });

  // Определяем кол-во показываемых уведомлений в зависимости от экрана
  const base = BASE_BY_BP[currentBreakpoint]; 

  const [showAll, setShowAll] = useState(false);

  const visibleNotifications = showAll 
    ? notificationList
    : notificationList.slice(0, base);

  useEffect(() => {
    const getNotificationData = async () => {
      try {
        const res = await api.post('/gates/data/get_user_push', {});

        const data = res.data;

        if (data.SUCCESS === true) {
          // Отображаем количество уведомлений
          setRead(data.DATA.COUNT);

          // Заполняем массив уведомлений
          setNotificationList(data.DATA.ROWS);

          sessionIdRef.current = data.DATA.SESSION_ID;
        }
      } catch (error) {
        console.error(error);
      } 
    }
    getNotificationData();
  }, [notifications]);

  const handleCheckNotification = async (notification) => {
    const typeNotif = notification.DOC_TYPE;
    const idNotif = notification.ID;
    const parentId = notification.PARENT_ID; // Для feedback - id обращения

    try {
      const res = await api.post('/gates/data/view_user_push', {
        DEL_REC_ID: idNotif
      });
      
      const data = res.data;

      if (data.SUCCESS === true) {
        // Отображаем количество уведомлений
        setRead(data.DATA.COUNT);
        // Заполняем массив уведомлений
        setNotificationList(data.DATA.ROWS);

        setOpen(false);
        const path = notificationTypes[typeNotif];
        if (typeNotif === 'FEEDBACK' && parentId) {
          // Если уже на странице обратной связи и есть глобальная функция – вызываем её
          if (window.location.pathname === '/chatFeedBack' && window.loadAppealById) {
            window.loadAppealById(parentId);
          } else {
            navigate(path, { state: { appealId: parentId } });
          }
        } else {
          navigate(path);
        }
      }

    } catch (error) {
      console.error(error);
    }
  }

  const getIconByType = (notification) => {
    const typeNotif = notification.DOC_TYPE;
    const Icon = iconByType[typeNotif];
    if (!Icon) return null;
    if (Icon) return <Icon size={20} />; 
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    setShowAll(false);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const formatDate = (serverDateString) => {
    const date = new Date(serverDateString);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  const formatTime = (serverDateString) => {
    const date = new Date(serverDateString);

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }
  
  return (
    <Box sx={{ flexShrink: 0 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={read} color="primary">
          <BellIcon/>
        </Badge>
      </IconButton>
      
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Уведомления"
                  elevation={0}
                  border={false}
                  content={false}
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: {
                        xs: '500px',
                        md: '700px'
                      },
                      overflowY: 'auto',  
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {notificationList.length === 0 && (
                      <Typography
                        textAlign={'center'}
                        sx={{
                          m: '1rem'
                        }}
                      >
                        Новых уведомлений нет
                      </Typography>
                    )}
                    
                    {visibleNotifications.map((item) => (
                      <ListItem
                        id={item.ID}
                        key={item.ID}
                        component={ListItemButton}
                        divider
                        selected={read > 0}
                        secondaryAction={
                          <Box
                            sx={{
                              width: '75px'
                            }}
                          >
                            <Typography 
                              variant="caption"
                            >
                              {`${formatDate(item.CRT_DATE)}\n`}
                            </Typography>
                            <Typography 
                              variant="caption"
                            >
                              {formatTime(item.CRT_DATE)}
                            </Typography>
                          </Box>
                          
                        }
                        onClick={() => handleCheckNotification(item)}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{
                              color: 'success.main',
                              bgcolor: 'transparent'
                            }}
                          >
                            {getIconByType(item)}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Typography variant='h6'>
                              {item.MESSAGE}
                            </Typography>
                          }
                          secondary="Нажмите, чтобы перейти в раздел"
                        />
                      </ListItem>
                    ))}
                    
                    {notificationList.length > base && !showAll && (
                      <ListItemButton 
                        onClick={() => setShowAll(true)}
                        sx={{ textAlign: 'center', py: `${12}px !important` }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="h6" color="rgd.primary">
                              Показать все
                            </Typography>
                          }
                        />
                      </ListItemButton> 
                    )}
                     
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
